import { z } from "zod";
import type { BlueprintDimension } from "./exam-config";

/**
 * Progress, gamification and mock-exam rules (spec §10.5). Values here are the seed defaults; XP and badges are
 * motivational only and never feed readiness or the forecast.
 */

// ---------------------------------------------------------------- XP and levels
export const XP = {
  card_graded: 1,
  question_answered: 2,
  question_correct_bonus: 1,
  session_completed: 10,
  daily_goal_met: 15,
  mock_completed: { quick: 25, standard: 50, full: 100 },
} as const;

/**
 * Anti-farming rules (plan §6.4, BE6). XP rewards real retrieval practice, so it only counts actions the server
 * can see were genuine: answers given at a reading pace, a question once per week, cards graded at a human pace,
 * sessions with real work in them, and mocks that were mostly answered. Timing uses server timestamps.
 */
export const XP_RULES = {
  /** An answer earns XP only if at least this long passed since the previous answer (or the session start). */
  min_answer_seconds: 3,
  /** A question earns XP once per student in this many days. */
  question_repeat_days: 7,
  /** A card grade earns XP only if at least this long after the previous grade. */
  min_card_gap_seconds: 2,
  /** Card XP per local day. */
  card_xp_daily_cap: 150,
  /** A session's completion bonus needs this many qualifying cards or answers. */
  min_items_for_session_bonus: 5,
  /** A mock earns its XP only when this share of its items was answered. */
  mock_min_answered_share: 0.8,
} as const;

/** Answers in time order (server `answered_at`), with whether each question already earned XP recently. */
export function qualifiedAnswers(
  startedAt: string,
  answers: Array<{ answered_at: string; correct: boolean; seen_recently: boolean }>,
): { answered: number; correct: number } {
  let previous = Date.parse(startedAt);
  let answered = 0;
  let correct = 0;
  for (const a of [...answers].sort((x, y) => x.answered_at.localeCompare(y.answered_at))) {
    const at = Date.parse(a.answered_at);
    if (at - previous >= XP_RULES.min_answer_seconds * 1000 && !a.seen_recently) {
      answered += 1;
      if (a.correct) correct += 1;
    }
    previous = at;
  }
  return { answered, correct };
}

/** Card grades in time order: each card once per run, at a human pace. */
export function qualifiedCardGrades(grades: Array<{ card_id: string; reviewed_at: string }>): number {
  const seen = new Set<string>();
  let previous = -Infinity;
  let count = 0;
  for (const g of [...grades].sort((x, y) => x.reviewed_at.localeCompare(y.reviewed_at))) {
    const at = Date.parse(g.reviewed_at);
    if (at - previous >= XP_RULES.min_card_gap_seconds * 1000 && !seen.has(g.card_id)) count += 1;
    seen.add(g.card_id);
    previous = at;
  }
  return count;
}

/** XP for a finished quiz session from its qualifying answers. */
export function quizSessionXp(q: { answered: number; correct: number }): number {
  const bonus = q.answered >= XP_RULES.min_items_for_session_bonus ? XP.session_completed : 0;
  return bonus + q.answered * XP.question_answered + q.correct * XP.question_correct_bonus;
}

/** XP for a flashcard run: capped card XP plus the bonus when enough cards qualified. */
export function flashcardRunXp(qualifiedCards: number, cardXpEarnedToday: number): { cards: number; bonus: number } {
  const cards = Math.max(0, Math.min(qualifiedCards * XP.card_graded, XP_RULES.card_xp_daily_cap - cardXpEarnedToday));
  return { cards, bonus: qualifiedCards >= XP_RULES.min_items_for_session_bonus ? XP.session_completed : 0 };
}

/** Mock XP only when most of it was answered (an abandoned mock earns nothing). */
export function mockXp(size: keyof typeof XP.mock_completed, answered: number, total: number): number {
  return total > 0 && answered / total >= XP_RULES.mock_min_answered_share ? XP.mock_completed[size] : 0;
}

/** Level n needs 100 × n × (n − 1) / 2 XP: L2 = 100, L3 = 300, L4 = 600 … */
export function levelForXp(xp: number): number {
  return Math.max(1, Math.floor((1 + Math.sqrt(1 + (8 * Math.max(0, xp)) / 100)) / 2));
}
export function xpForLevel(level: number): number {
  return (100 * level * (level - 1)) / 2;
}

export const LEVEL_NAMES = [
  "First Rounds",
  "Quick Learner",
  "Steady Studier",
  "Clinical Thinker",
  "Sharp Reasoner",
  "Exam Ready",
  "Board Strategist",
  "Hoot Expert",
] as const;
export function levelName(level: number): string {
  return LEVEL_NAMES[Math.min(level, LEVEL_NAMES.length) - 1] ?? LEVEL_NAMES[0];
}

// ---------------------------------------------------------------- badges
export const BADGES = {
  first_set: { name: "First set", detail: "Built your first study set", xp: 20 },
  first_mock: { name: "First mock", detail: "Finished your first mock exam", xp: 20 },
  streak_7: { name: "7-day streak", detail: "Studied 7 days in a row", xp: 30 },
  streak_30: { name: "30-day streak", detail: "Studied 30 days in a row", xp: 100 },
  streak_100: { name: "100-day streak", detail: "Studied 100 days in a row", xp: 300 },
  cards_100: { name: "100 cards", detail: "Reviewed 100 flashcards", xp: 20 },
  cards_1000: { name: "1,000 cards", detail: "Reviewed 1,000 flashcards", xp: 100 },
  perfect_quiz: { name: "Perfect quiz", detail: "100% on a quiz of 10 or more", xp: 25 },
  // Key kept for badges already earned; renamed by the owner (OD-7) — a nod to night shifts, not a goal to stay up.
  night_owl: { name: "Night Shift", detail: "Studied after 10 pm", xp: 10 },
  early_bird: { name: "Early bird", detail: "Studied between 5 am and 8 am", xp: 10 },
  full_mock: { name: "Full-length finisher", detail: "Completed a full-length exam simulation", xp: 50 },
  // Learning milestones (plan §6.2, BE21): reward retrieval practice and breadth, not time in the app.
  questions_100: { name: "100 questions", detail: "Answered 100 practice questions", xp: 20 },
  questions_500: { name: "500 questions", detail: "Answered 500 practice questions", xp: 60 },
  case_study: { name: "First case study", detail: "Worked through all six items of an NGN case study", xp: 30 },
  all_client_needs: { name: "Every Client Need", detail: "Practised questions in all eight Client Needs categories", xp: 40 },
} as const;
export type BadgeKey = keyof typeof BADGES;
export const badgeKeySchema = z.enum(Object.keys(BADGES) as [BadgeKey, ...BadgeKey[]]);

/** Badges a finished study session can earn, from its own facts (the caller skips ones already earned). */
export function sessionBadges(input: {
  localHour: number;
  questions: number;
  scorePercent: number | null;
  totalCardsReviewed: number;
  streak: number;
  totalQuestionsAnswered?: number;
  completedCaseStudy?: boolean;
  coveredAllCategories?: boolean;
}): BadgeKey[] {
  const out: BadgeKey[] = [];
  if ((input.totalQuestionsAnswered ?? 0) >= 100) out.push("questions_100");
  if ((input.totalQuestionsAnswered ?? 0) >= 500) out.push("questions_500");
  if (input.completedCaseStudy) out.push("case_study");
  if (input.coveredAllCategories) out.push("all_client_needs");
  if (input.localHour >= 22 || input.localHour < 4) out.push("night_owl");
  if (input.localHour >= 5 && input.localHour < 8) out.push("early_bird");
  if (input.questions >= 10 && input.scorePercent === 100) out.push("perfect_quiz");
  if (input.totalCardsReviewed >= 100) out.push("cards_100");
  if (input.totalCardsReviewed >= 1000) out.push("cards_1000");
  if (input.streak >= 7) out.push("streak_7");
  if (input.streak >= 30) out.push("streak_30");
  if (input.streak >= 100) out.push("streak_100");
  return out;
}

// ---------------------------------------------------------------- streaks
export interface StreakState {
  current: number;
  best: number;
  freezes: number;
  last_active_date: string | null;
}
export const MAX_FREEZES = 2;
const dayNumber = (isoDate: string) => Math.round(Date.parse(`${isoDate}T00:00:00Z`) / 86_400_000);

/** YYYY-MM-DD for an instant in the user's timezone. */
export function localDate(at: Date, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(at);
}
export function localHour(at: Date, timeZone: string): number {
  return Number(new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", hourCycle: "h23" }).format(at));
}

/**
 * A day counts when the student completes a session in their own timezone. Missed days use banked freezes;
 * one freeze is earned per 7 consecutive days, at most 2 banked (§10.5).
 */
export function advanceStreak(state: StreakState, today: string): StreakState {
  if (state.last_active_date === today) return state;
  let { current, freezes } = state;
  if (state.last_active_date === null) current = 1;
  else {
    const missed = dayNumber(today) - dayNumber(state.last_active_date) - 1;
    if (missed < 0) return state;
    if (missed === 0) current += 1;
    else if (missed <= freezes) {
      freezes -= missed;
      current += 1;
    } else current = 1;
  }
  if (current > 0 && current % 7 === 0) freezes = Math.min(MAX_FREEZES, freezes + 1);
  return { current, best: Math.max(state.best, current), freezes, last_active_date: today };
}

/** Whether the streak is still alive today (not broken beyond what freezes can cover). */
export function liveStreak(state: StreakState, today: string): number {
  if (!state.last_active_date) return 0;
  const missed = dayNumber(today) - dayNumber(state.last_active_date) - 1;
  return missed <= state.freezes ? state.current : 0;
}

// ---------------------------------------------------------------- readiness & forecast
export const MIN_ANSWERS_FOR_READINESS = 30;

export interface CategoryReadiness {
  key: string;
  name: string;
  weight: number;
  /** 0–1, or null when there's nothing to go on for this category yet. */
  value: number | null;
  answered: number;
}

/**
 * Readiness per blueprint category = mean of (a) FSRS retrievability of its cards and (b) recency-weighted
 * question accuracy over the last 30 days; overall = blueprint-weighted mean (§10.5).
 */
export function computeReadiness(input: {
  dimension: BlueprintDimension;
  cards: Array<{ category: string | undefined; retrievability: number }>;
  answers: Array<{ category: string | undefined; score: number; answered_at: string }>;
  now: Date;
}): { categories: CategoryReadiness[]; overall: number | null; answered: number } {
  const since = input.now.getTime() - 30 * 86_400_000;
  const recent = input.answers.filter((a) => Date.parse(a.answered_at) >= since);
  const categories = input.dimension.categories.map((cat) => {
    const cards = input.cards.filter((c) => c.category === cat.key);
    const answers = recent.filter((a) => a.category === cat.key);
    const recall = cards.length ? cards.reduce((n, c) => n + c.retrievability, 0) / cards.length : null;
    let accuracy: number | null = null;
    if (answers.length) {
      // Recency weight: an answer today counts 1, one from 30 days ago about 0.37.
      const weights = answers.map((a) => Math.exp(-(input.now.getTime() - Date.parse(a.answered_at)) / (30 * 86_400_000)));
      accuracy = answers.reduce((n, a, i) => n + a.score * weights[i]!, 0) / weights.reduce((n, w) => n + w, 0);
    }
    const parts = [recall, accuracy].filter((v): v is number => v !== null);
    return { key: cat.key, name: cat.name, weight: cat.weight, value: parts.length ? parts.reduce((a, b) => a + b, 0) / parts.length : null, answered: answers.length };
  });
  const known = categories.filter((c) => c.value !== null);
  const totalWeight = known.reduce((n, c) => n + c.weight, 0);
  const overall = known.length && totalWeight > 0 ? known.reduce((n, c) => n + c.value! * c.weight, 0) / totalWeight : null;
  return { categories, overall, answered: recent.length };
}

/** A readiness range (never a single "pass probability"): ±1.96 standard errors on the answered sample. */
export function readinessRange(value: number, answered: number): { low: number; high: number } {
  const se = Math.sqrt(Math.max(value * (1 - value), 0.01) / Math.max(answered, 1));
  return { low: Math.max(0, value - 1.96 * se), high: Math.min(1, value + 1.96 * se) };
}

/** Forecast (Pro): today's readiness projected to the exam date at the last 14 days' pace, as a range (§10.5). */
export function forecastRange(input: { now: number; twoWeeksAgo: number | null; daysLeft: number; answered: number }): { low: number; high: number; basis: string } {
  const perDay = input.twoWeeksAgo === null ? 0 : (input.now - input.twoWeeksAgo) / 14;
  // Gains slow as readiness rises; cap the projected change so a good fortnight can't promise the moon.
  const projected = Math.max(0, Math.min(1, input.now + Math.max(-0.3, Math.min(0.3, perDay * Math.max(0, input.daysLeft)))));
  const spread = readinessRange(projected, input.answered);
  const widen = Math.min(0.15, 0.002 * Math.max(0, input.daysLeft));
  return {
    low: Math.max(0, spread.low - widen),
    high: Math.min(1, spread.high + widen),
    basis: input.twoWeeksAgo === null ? "Based on your readiness today; keep studying for a trend." : `Based on your last 14 days' pace over ${Math.max(0, input.daysLeft)} days.`,
  };
}

// ---------------------------------------------------------------- mock assembly (§10.5)
export const MOCK_SIZES = ["quick", "standard", "full"] as const;
export const mockSizeSchema = z.enum(MOCK_SIZES);
export type MockSize = z.infer<typeof mockSizeSchema>;
export const MOCK_DEFAULTS = { quick: { items: 25, minutes: 30 }, standard: { items: 75 } } as const;

export interface PoolItem {
  id: string;
  item_type: string;
  category: string | undefined;
  case_id: string | null;
  difficulty: number;
  stage_order: number;
  recent: boolean;
}

/**
 * Picks `count` units blueprint-weighted across categories, avoiding items seen in the last 7 days when the
 * pool allows. A unit is one stand-alone item or one whole case (all its stages, in order).
 */
export function assembleBlueprintForm(pool: PoolItem[], dimension: BlueprintDimension, count: number, random = Math.random): string[] {
  const units = new Map<string, PoolItem[]>();
  for (const item of pool) units.set(item.case_id ?? item.id, [...(units.get(item.case_id ?? item.id) ?? []), item]);
  const all = [...units.values()].map((u) => u.sort((a, b) => a.stage_order - b.stage_order));
  const shuffled = (list: PoolItem[][]) =>
    list
      .map((u) => ({ u, k: (u.some((i) => i.recent) ? 1 : 0) + random() }))
      .sort((a, b) => a.k - b.k)
      .map((x) => x.u);
  const totalWeight = dimension.categories.reduce((n, c) => n + c.weight, 0);
  const picked: PoolItem[][] = [];
  const used = new Set<PoolItem[]>();
  for (const cat of dimension.categories) {
    const want = Math.round((cat.weight / totalWeight) * count);
    for (const u of shuffled(all.filter((u) => u[0]!.category === cat.key)).slice(0, want)) {
      picked.push(u);
      used.add(u);
    }
  }
  // Top up from any category (short categories, rounding), then trim to size.
  for (const u of shuffled(all.filter((u) => !used.has(u)))) {
    if (picked.length >= count) break;
    picked.push(u);
  }
  return picked.slice(0, count).flatMap((u) => u.map((i) => i.id));
}

// ---------------------------------------------------------------- adaptive (NCLEX CAT practice, 1PL / Rasch)
const logistic = (x: number) => 1 / (1 + Math.exp(-x));

/** Ability estimate after each answer: one Newton step on the Rasch log-likelihood, clamped to ±4. */
export function updateAbility(theta: number, answers: Array<{ difficulty: number; correct: boolean; score?: number }>): { theta: number; se: number } {
  let t = theta;
  for (let i = 0; i < 10; i++) {
    let grad = 0;
    let info = 0;
    for (const a of answers) {
      const p = logistic(t - a.difficulty);
      // Partial-credit items (NGN) count their share of credit, not just right/wrong.
      grad += (a.score ?? (a.correct ? 1 : 0)) - p;
      info += p * (1 - p);
    }
    if (info < 1e-6) break;
    t = Math.max(-4, Math.min(4, t + grad / info));
  }
  const info = answers.reduce((n, a) => n + logistic(t - a.difficulty) * (1 - logistic(t - a.difficulty)), 0);
  return { theta: t, se: info > 0 ? 1 / Math.sqrt(info) : 4 };
}

/** Next item: unseen, closest in difficulty to the current ability. */
export function nextAdaptiveItem(pool: PoolItem[], seen: Set<string>, theta: number): PoolItem | null {
  return pool.filter((p) => !seen.has(p.id) && !p.case_id).sort((a, b) => Math.abs(a.difficulty - theta) - Math.abs(b.difficulty - theta) || Number(a.recent) - Number(b.recent))[0] ?? null;
}

/**
 * NCLEX-style adaptive plan (plan §7.1, BE13). Every real exam has its case studies inside the minimum length and
 * 15 unscored pretest items among the stand-alone ones; we place the cases at evenly spread points and mark pretest
 * positions at random. Positions are 0-based item numbers.
 */
export function caseSlots(minItems: number, cases: number): number[] {
  return Array.from({ length: cases }, (_, i) => Math.floor((minItems * (i + 1)) / (cases + 1)) - 3);
}

export function pretestPositions(minItems: number, count: number, caseStarts: number[], itemsPerCase: number, random = Math.random): number[] {
  const inCase = (n: number) => caseStarts.some((s) => n >= s && n < s + itemsPerCase);
  const open = Array.from({ length: minItems }, (_, n) => n).filter((n) => !inCase(n));
  for (let i = open.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [open[i], open[j]] = [open[j]!, open[i]!];
  }
  return open.slice(0, count).sort((a, b) => a - b);
}

/**
 * What an adaptive mock serves next: an already-served item still waiting (the rest of a case), a whole case when
 * its slot comes up, an adaptive pick, or stop. Never stops in the middle of a case.
 */
export function adaptiveStep(input: {
  /** Items served so far (answered or waiting). */
  served: number;
  /** A served item still waits for its answer (the rest of a case). */
  waiting: boolean;
  caseStarts: number[];
  casesServed: number;
  /** Whole, unseen cases left in the student's pool. */
  casesAvailable: number;
  shouldStop: boolean;
}): "continue_case" | "serve_case" | "pick" | "stop" {
  if (input.waiting) return "continue_case";
  const slot = input.caseStarts[input.casesServed];
  if (slot !== undefined && input.served >= slot && input.casesAvailable > 0) return "serve_case";
  return input.shouldStop ? "stop" : "pick";
}

/**
 * The real exam's three stopping rules (NCSBN 2026 test plan): 95% confidence clear of the passing line after the
 * minimum, the maximum length, and running out of time — below the minimum that's a fail, otherwise the final
 * estimate decides. Our item difficulties come from AI, so the "line" is an estimate, never a pass prediction.
 */
export function adaptiveOutcome(input: { answered: number; theta: number; se: number; min: number; timedOut: boolean }): "above" | "below" | "undetermined" {
  if (input.timedOut) return input.answered < input.min ? "below" : input.theta >= 0 ? "above" : "below";
  if (input.theta - 1.96 * input.se > 0) return "above";
  if (input.theta + 1.96 * input.se < 0) return "below";
  return "undetermined";
}

/**
 * A fixed NGN practice form (plan BE13): up to `cases` whole case studies (all `caseSize` items, in stage order) at
 * the spread-out slots, the rest blueprint-weighted stand-alone items. Fewer cases when the pool has fewer.
 */
export function assembleNgnForm(
  pool: PoolItem[],
  dimension: BlueprintDimension,
  total: number,
  cases: number,
  caseSize: number,
  random = Math.random,
): { ids: string[]; cases: number } {
  const byCase = new Map<string, PoolItem[]>();
  for (const item of pool) if (item.case_id) byCase.set(item.case_id, [...(byCase.get(item.case_id) ?? []), item]);
  const whole = [...byCase.values()]
    .filter((c) => c.length === caseSize)
    .map((c) => c.sort((a, b) => a.stage_order - b.stage_order))
    .map((c) => ({ c, k: (c.some((i) => i.recent) ? 1 : 0) + random() }))
    .sort((a, b) => a.k - b.k)
    .slice(0, cases)
    .map((x) => x.c);
  const standalone = assembleBlueprintForm(pool.filter((i) => !i.case_id), dimension, total - whole.length * caseSize, random);
  const starts = caseSlots(total, whole.length);
  const ids: string[] = [];
  let next = 0;
  for (const c of whole) {
    const at = starts[next++]!;
    while (ids.length < at && standalone.length) ids.push(standalone.shift()!);
    ids.push(...c.map((i) => i.id));
  }
  ids.push(...standalone);
  return { ids, cases: whole.length };
}

/** Stops once the 95% interval clears the passing line (0) after the minimum, or at the maximum. */
export function adaptiveShouldStop(answered: number, theta: number, se: number, range: { min: number; max: number }): boolean {
  if (answered >= range.max) return true;
  return answered >= range.min && Math.abs(theta) > 1.96 * se;
}
